<template>
	<app-page>
		<app-heading :title="complex.name" />
		<app-form @submit="onSubmit">
			<app-section class="mt-4">
				<app-section-actions>
					<app-button type="submit" small color="primary">Save</app-button>
					<app-button
						type="cancel"
						small
						:to="{ name: 'website-complexes', params: { website: websiteId } }"
					>
						Cancel
					</app-button>
				</app-section-actions>
				<div class="title">Language</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-select
							v-model="locale"
							:items="locales"
							label="Language"
						/>
					</app-cell>
				</app-row>
				<div class="title">URL</div>
				<app-row>
					<app-cell cols="12" sm="4">
						<input-text
							v-if="locale === 'en'"
							label="URI"
							v-model="uriName"
							required
							:errors="errors.uri_name"
						/>
						<input-text v-else
							label="Translated URI"
							:placeholder="uriName"
							v-model="uriTranslations[locale]"
							hint="Write a new URI only if you intent to translate the original URI"
							:errors="errors.uri_translations"
						/>
					</app-cell>
				</app-row>
				<app-divider class="ma-4" />
				<div class="title">
					<span>SEO</span>
					<app-button
						color="secondary"
						small
						flat
						class="ml-2"
						@click="onDetectConflicts"
					>Detect conflicts</app-button>
				</div>
				<div v-if="locale">
					<app-row
						v-for="(conflict, i) in getConflicts('title')"
						:key="`conflict-title-${i}`"
					>
						<app-cell cols="12" sm="6">
							<fragment-seo-conflict :conflict="conflict" />
						</app-cell>
					</app-row>
					<app-row>
						<app-cell cols="12" sm="6">
							<input-textarea
								label="Meta title"
								v-model="meta.title[locale]"
								:placeholder="complex.translations
									? (complex.translations[locale] || complex.translations.en)
									: complex.name"
								required
								single-line
								counter="60"
								:errors="errors.title"
							/>
						</app-cell>
					</app-row>
					<app-row
						v-for="(conflict, i) in getConflicts('description')"
						:key="`conflict-description-${i}`"
					>
						<app-cell cols="12" sm="6">
							<fragment-seo-conflict :conflict="conflict" />
						</app-cell>
					</app-row>
					<app-row>
						<app-cell cols="12" sm="6">
							<input-textarea
								label="Meta description"
								v-model="meta.description[locale]"
								:placeholder="complex.short_description
									? complex.short_description[locale] || complex.short_description.en
									: null"
								required
								counter="160"
								:errors="errors.description"
							/>
						</app-cell>
					</app-row>
					<app-row
						v-for="(conflict, i) in getConflicts('heading')"
						:key="`conflict-heading-${i}`"
					>
						<app-cell cols="12" sm="6">
							<fragment-seo-conflict :conflict="conflict" />
						</app-cell>
					</app-row>
					<app-row>
						<app-cell cols="12" sm="6">
							<input-textarea
								label="Heading (H1)"
								v-model="meta.heading[locale]"
								:placeholder="complex.translations
									? (complex.translations[locale] || complex.translations.en)
									: complex.name"
								required
								single-line
								counter="70"
								:errors="errors.heading"
							/>
						</app-cell>
					</app-row>
					<app-row
						v-for="(conflict, i) in getConflicts('heading2')"
						:key="`conflict-heading2-${i}`"
					>
						<app-cell cols="12" sm="6">
							<fragment-seo-conflict :conflict="conflict" />
						</app-cell>
					</app-row>
					<app-row>
						<app-cell cols="12" sm="6">
							<input-textarea
								label="Sub-heading (H2)"
								v-model="meta.heading2[locale]"
								required
								counter="200"
								rows="2"
								:errors="errors.heading2"
							/>
						</app-cell>
					</app-row>
				</div>
			</app-section>
		</app-form>
	</app-page>
</template>

<script>
import { mapValues } from 'lodash'

import locales from '@/datasources/locales'
import FragmentSeoConflict from '@/components/fragments/FragmentSeoConflict.vue'
import { clearEmptyFields } from '@/utils/collection'

export default {
	components: { FragmentSeoConflict },
	data() {
		return {
			locale: null,
			website: {
				locales: [],
			},
			complex: {
				name: '',
				translations: {},
				description: {},
			},
			errors: {},
			conflicts: [],
			meta: {
				title: {},
				description: {},
				heading: {},
				heading2: {},
			},
			uriName: '',
			uriTranslations: {},
		}
	},
	async mounted() {
		this.$store.dispatch('app/startLoading')
		this.website = await this.$ravello.graphql('website', this.websiteId)
			.fields(['locales'])
			.include(this.$ravello.graphql('complex', this.id)
				.fields(['uri_name', 'uri_translations'])
				.include(this.$ravello.graphql('meta').fields([
					'title',
					'description',
					'heading',
					'heading2',
				]))
				.include(this.$ravello.graphql('complex').fields([
					'name',
					'translations',
					'short_description',
				])))
			.query()
		this.complex = this.website.complex.complex

		this.meta = mapValues(this.website.complex.meta, (value) => {
			const metaValue = value ? { ...value } : {}

			this.website.locales.forEach((locale) => {
				if (!metaValue[locale]) {
					metaValue[locale] = ''
				}
			})
			return metaValue
		})
		this.uriName = this.website.complex.uri_name
		this.uriTranslations = this.website.complex.uri_translations
			? { ...this.website.complex.uri_translations } : {}

		this.website.locales.forEach((locale) => {
			if (locale === 'en') { return }

			if (!this.uriTranslations[locale]) {
				this.uriTranslations[locale] = ''
			}
		})

		const [locale] = this.website.locales
		this.locale = locale
		this.$store.dispatch('app/stopLoading')
	},
	computed: {
		websiteId() {
			return this.$route.params.website
		},
		id() {
			return this.$route.params.complex
		},
		locales() {
			return this.website.locales.map(locale => ({
				text: locales[locale].name,
				value: locale,
			}))
		},
	},
	methods: {
		async injectConflictedComplexes(conflicts) {
			const ids = Object.keys(conflicts.reduce((acc, conflict) => ({
				...acc,
				[conflict.complex]: true,
			}), {}))

			if (!ids.length) { return [] }
			const complexes = await this.$ravello.graphql('complexes')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.include(this.$ravello.graphql('complex').fields(['_id', 'name']))
				.filter('complex', ids)
				.limit(ids.length)
				.query()

			const complexesMap = complexes.reduce((acc, complex) => ({
				...acc,
				[complex.complex._id]: complex.complex,
			}), {})

			return conflicts.map(conflict => ({
				...conflict,
				res: complexesMap[conflict.complex] || {},
			}))
		},
		async onDetectConflicts() {
			this.$store.dispatch('app/startLoading')
			try {
				const conflicts = await this.$ravello.request().v(2)
					.to('websites')
					.to(this.websiteId)
					.to('complexes')
					.to(this.id)
					.to('detectconflicts')
					.create({
						...this.meta,
					})

				this.conflicts = await this.injectConflictedComplexes(conflicts)
				this.$store.dispatch('app/stopLoading')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
				this.$store.dispatch('app/stopLoading')
			}
		},

		getConflicts(field) {
			return (this.conflicts || []).filter(conflict => conflict.field === field)
		},

		async onSubmit() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request().v(2)
					.to('websites')
					.to(this.websiteId)
					.to('complexes')
					.to(this.id)
					.update({
						meta: this.meta,
						uri_name: this.uriName,
						uri_translations: clearEmptyFields(this.uriTranslations),
					})
				this.$store.dispatch('app/stopLoading')
				this.$notifications.pushAlert('success', `${this.complex.name} meta successfully updated`)
				this.$router.push({ name: 'website-complexes', params: { website: this.websiteId } })
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
				if (err.status === 400) {
					this.errors = err.data.details || {}
				}
				this.$store.dispatch('app/stopLoading')
			}
		},
	},
}
</script>
